import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/Carousel";
import { H2 } from "@/components/ui/Heading";
import { useSearch } from "@/contexts/SearchContext";
import { Locale } from "@/enums/common.enum";
import { BoostType, PropertyListingViewType } from "@/enums/property.enum";
import { Link } from "@/i18n/routing";
import { Property } from "@/interfaces/property.interface";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import React, { useEffect } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { PropertyListViewProps } from "..";
import { CardHeader } from "./CardHeader";
import { ContentSection } from "./ContentSection";
import { PropertyLastUpdatedBadge } from "./LastUpdated";
import { PropertyMediaInfo } from "./MediaInfo";
import { PremiumCardHeader } from "./PremiumCardHeader";

export interface PropertyViewCardProps {
  listingId: string;
  slotProps: {
    link: React.ComponentProps<typeof Link>;
  };
  index: number;
  property: Property;
  variant?: PropertyListViewProps["variant"];
  onViewChange?: (property: Property, state: "on" | "off") => void;
}

export const PropertyViewCard = (props: PropertyViewCardProps) => {
  const { listingId, slotProps, property, variant, onViewChange, index } =
    props;

  const locale = useLocale() as Locale;
  const { view } = useSearch();

  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.9,
  });

  const isMapView = view === PropertyListingViewType.MAP;
  const title = property.description[locale].title;

  useEffect(() => {
    onViewChange?.(property, isIntersecting ? "on" : "off");
  }, [isIntersecting, property]);

  switch (property.boostType) {
    case BoostType.PREMIUM:
      return (
        <Link {...slotProps.link}>
          <div
            className="rounded-xl overflow-hidden shadow-sm border"
            ref={ref}
          >
            <PremiumCardHeader {...{ property }} index={index} />

            <div className="relative overflow-hidden">
              <Carousel className="w-full">
                <CarouselContent>
                  {property.photos.map((image, index) => (
                    <CarouselItem key={index}>
                      <div className="relative w-full overflow-hidden h-[400px]">
                        <Image
                          src={image}
                          fill
                          unoptimized={image.startsWith("https")}
                          alt={`${title}-${index + 1}`}
                          className=" w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          sizes="(max-width: 768px) 80vw, 50vw"
                          fetchPriority={index === 0 ? "high" : "low"}
                          priority={index === 0 ? true : false}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="absolute left-4 top-1/2 h-8 w-8 -translate-y-1/2 bg-white/80 backdrop-blur-sm" />
                <CarouselNext className="absolute right-4 top-1/2 h-8 w-8 -translate-y-1/2 bg-white/80 backdrop-blur-sm" />
              </Carousel>
              <PropertyLastUpdatedBadge property={property} />
              <PropertyMediaInfo property={property} />
            </div>

            <H2 className="hidden"> {title}</H2>
            <ContentSection
              listingId={listingId}
              property={property}
              containerClassName="py-4 px-5"
            />
          </div>
        </Link>
      );
    default:
      return (
        <Link {...slotProps.link}>
          <div
            ref={ref}
            className={clsx(
              "rounded-xl overflow-hidden shadow-sm border flex flex-col w-full",
              {
                "sm:flex-row": variant === "normal",
              }
            )}
          >
            <div className={clsx("relative overflow-hidden w-full")}>
              {property.boostType === BoostType.FEATURED && (
                <CardHeader {...{ property }} index={props.index} />
              )}
              <Carousel className="w-full">
                <CarouselContent>
                  {property.photos?.map((image, index) => (
                    <CarouselItem key={index}>
                      <div className="relative w-full overflow-hidden h-[195px]">
                        <Image
                          src={image}
                          fill
                          alt={`${title}-${index + 1}`}
                          unoptimized={image.startsWith("https")}
                          className=" w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          sizes="(max-width: 768px) 80vw, 20vw"
                          fetchPriority={index === 0 ? "high" : "low"}
                          priority={index === 0 ? true : false}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="absolute left-4 top-1/2 h-8 w-8 -translate-y-1/2 bg-white/80 backdrop-blur-sm" />
                <CarouselNext className="absolute right-4 top-1/2 h-8 w-8 -translate-y-1/2 bg-white/80 backdrop-blur-sm" />
              </Carousel>
              <PropertyLastUpdatedBadge property={property} />
              <PropertyMediaInfo property={property} />
            </div>
            <H2 className="hidden"> {title}</H2>

            <ContentSection
              listingId={listingId}
              property={property}
              containerClassName={clsx("w-full", {
                "px-2 py-3": isMapView,
                "p-5": !isMapView,
              })}
            />
          </div>
        </Link>
      );
  }
};
